// Note: These types are copied from the backend

// TODO consolidate user.ts and set the proper user form types in there...

import { UserState } from './user-state';
import { LicenseState } from '@/core/services/utils/license-state';
import { OnboardingService } from '@/home/onboarding/onboarding.service';
import { User } from '@/core/models/user';
import { LogoOptionType, PdfOrientationType, RoundingType } from '@/core/models/report-settings.interface';
import { TimeFormat } from './time-format';

interface ColumnDto {
    id: number | null;
    visible: boolean | null;
}

export interface ReportSettingsDto {
    dateFormat: string | null;
    timeFormat: TimeFormat | null;
    tableColumns: ColumnDto[] | null;
    durationFormat: string | null;
    durationRoundingType: RoundingType | null; // 'none' can be used to represent no rounding
    durationRoundingUnit: number | null; // The unit in minutes, e.g., 1, 5, 15, 30
    roundingEnabled: boolean | null; // Whether to round the time or Not
    logoOption: LogoOptionType | null;
    heading: string | null;
    pdfOrientation: PdfOrientationType | null;
}

export interface UserSettingsForm {
    color: string;
    language: string;
    dateFormat: string;
    timeFormat: TimeFormat;
    apiKey: string;
    startWeekWithSunday: boolean;
    timeRasterization: number | null;
    tileColorFromLayer: number | null;
    reportSettings: ReportSettingsDto;
}

export enum UserPermission {
    ACTIVITY_READ = 'ACTIVITY_READ',
    ACTIVITY_EDIT = 'ACTIVITY_EDIT',
    ACTIVITY_CREATE = 'ACTIVITY_CREATE',
    ACTIVITY_DELETE = 'ACTIVITY_DELETE',
    ACTIVITY_READ_TEAM = 'ACTIVITY_READ_TEAM',
    ACTIVITY_EDIT_TEAM = 'ACTIVITY_EDIT_TEAM',
    ACTIVITY_CREATE_TEAM = 'ACTIVITY_CREATE_TEAM',
    ACTIVITY_DELETE_TEAM = 'ACTIVITY_DELETE_TEAM',
    API_KEY = 'API_KEY',
    TILE_READ = 'TILE_READ',
    TILE_EDIT = 'TILE_EDIT',
    TILE_FAV = 'TILE_FAV',
    TILE_CREATE = 'TILE_CREATE',
    TILE_ARCHIVE = 'TILE_ARCHIVE',
    LAYER_READ = 'LAYER_READ',
    LAYER_EDIT = 'LAYER_EDIT',
    LAYER_CREATE = 'LAYER_CREATE',
    REPORT_CREATE = 'REPORT_CREATE',
    TEMPLATE_READ = 'TEMPLATE_READ',
    TEMPLATE_EDIT = 'TEMPLATE_EDIT',
    TEMPLATE_CREATE = 'TEMPLATE_CREATE',
    USER_CREATE = 'USER_CREATE',
    USER_EDIT = 'USER_EDIT',
    USER_READ = 'USER_READ',
    USER_EDIT_PERMISSION = 'USER_EDIT_PERMISSION',
    INTERFACE_CONFIG = 'INTERFACE_CONFIG',
    WEBHOOK_CONFIG = 'WEBHOOK_CONFIG',
    MANAGE_SUBSCRIPTION = 'MANAGE_SUBSCRIPTION',
}

// TODO everything is optional in the backend. That does not look too good...
//     changed most values to be not optional
export interface UserForm {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    permissions?: UserPermission[];
    state: UserState; // TODO: This is only a string in the backend! The dev database contains empty strings :-o
    accountId?: number;
    templateId?: number;
    settings?: UserSettingsForm;
    password?: string;
    trialStart?: Date;
}

/** Writable fields for the legacy PUT endpoints, which preserve omitted user fields. */
export type UserUpdateForm = Partial<
    Pick<
        UserForm,
        'email' | 'firstName' | 'lastName' | 'permissions' | 'state' | 'templateId' | 'settings' | 'password'
    >
>;

// Note: all fields in the API call are optional and only those set will be written to the database.
export interface AccountSettingsForm {
    companyName?: string;
    currency?: string;
    onboarding?: string;
    retroactiveEditLimit?: number | null;
    // switch for the time budget feature; off by default, changed by admins only
    timeBudgetsEnabled?: boolean;
    // lets regular users see the switched-on budgets of their tiles; off by default, changed by admins only
    timeBudgetsVisibleToAllUsers?: boolean;
}

export interface AccountForm {
    id: number;
    licenses: number;
    consumedLicenses_: number;
    trialStart: Date | null;
    accountSettings: AccountSettingsForm;
    users: UserForm[];
}

export class Account implements AccountForm {
    accountSettings: AccountSettingsForm = {
        companyName: '',
        currency: '',
        onboarding: '',
    };
    users: UserForm[] = []; // TODO - this should be type "User"
    id: number = 0;
    licenses: number = 0;
    consumedLicenses_: number = 0;
    trialStart: Date | null = null;

    public static fromApi(dto: AccountFormDto): Account {
        const account = new Account();
        account.accountSettings = dto.accountSettings;
        // TODO User.fromJson(element)
        account.users = dto.users;
        account.id = dto.id;
        account.licenses = dto.licenses;
        account.consumedLicenses_ = dto.consumedLicenses_;
        account.trialStart = dto.trialStart ? new Date(dto.trialStart) : null;
        return account;
    }

    public getActiveUserForms(): UserForm[] {
        return this.users.filter((user) => user.state === UserState.Active);
    }

    // TODO optimize - this always converts the UserForm objects on the fly to User objects
    public getActiveUsers(): User[] {
        return this.getUsers().filter((user) => user.state === UserState.Active);
    }

    public getTrialDaysLeft(): number {
        if (this.trialStart === null) {
            return 0;
        }
        return LicenseState.calculateTrialDaysLeft(this.trialStart);
    }

    public getOnboardingSteps(): boolean[] | undefined {
        return OnboardingService.getOnboardingSteps(this);
    }

    public getActiveUserCount(): number {
        return this.getActiveUserForms().length;
    }

    public hasEnoughProLicenses(): boolean {
        return this.licenses >= this.consumedLicenses_;
    }

    // TODO - temporary function - remove later when mapping is always performed
    public getUsers(): User[] {
        return this.users.map((u: UserForm) => User.fromJson(u));
    }

    public isSingleUserAccount(): boolean {
        return this.users.length === 1;
    }
}

// see account.ts AccountForm
export interface AccountFormDto {
    id: number;
    licenses: number;
    consumedLicenses_: number;
    trialStart: Date | string | null;
    accountSettings: AccountSettingsForm;
    users: UserForm[];
}
